import { Tooltip } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { toastError, toastSuccess, toastWarning } from '@components/Toast';
import LoaderComponent from '@components/LoaderComponent';

import { generateRandNumber } from '@features/crm/utils/helpers';
import {
	useGetSampleIdsByProductIdQuery,
	useLazyAssignSamplesToProductQuery,
	useLazyMarkPresentationAsDefaultQuery
} from '@features/designTool/api/designToolApi';

import { productWiseGroupDocument } from '@store/actions/DesignToolAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import ApiClient from '@lib/apiClient';

import { checkAnyUserType, getShortName, isValidFile, printApiError } from '@utils/Util';

import { BASE_URL } from '@/constants/constant';

import VirtualTryOnSidebar from '@/features/designTool/components/VirtualTryOnSidebar';
import AiModelCard from '@/features/designTool/components/AiModelCard';
import CommonImage from '@/components/CommonImage';
import { hidePresentation } from '@/services/presentationService';

const Presentation = ({ setLoader }) => {
	const designToolCommentStore = useSelector((store) => store.designToolCommentStore);
	const designToolStoreState = useSelector((store) => store.designToolStore);
	// Keep a local snapshot so data persists even if the shared store is cleared (e.g. on DesignView close).
	const [designToolStore, setDesignToolStore] = useState(designToolStoreState);
	const [psdItems, setPsdItems] = useState(['']);
	const isAdminOrPhotographer = checkAnyUserType(['ADMIN', 'PHOTOGRAPHY']);
	const isUserCanHidePresentation = checkAnyUserType([
		'ADMIN',
		'FASHION_DESIGNER',
		'PRODUCT_DEVELOPMENT',
		'ACCOUNT_MANAGER'
	]);
	const dispatch = useDispatch();
	const { id } = useParams();
	const [isOpenSidebar, setIsOpenSidebar] = useState(false);
	const [isHiddenPresentation, setIsHiddenPresentation] = useState(false);
	const [presentations, setPresentations] = useState([]);
	const { data: sampleIdList, isLoading } = useGetSampleIdsByProductIdQuery({ productId: id });
	const [assignSamplesToProduct, { isLoading: assignLoading }] = useLazyAssignSamplesToProductQuery();
	const [markPresentationAsDefault] = useLazyMarkPresentationAsDefaultQuery();

	const renderEmptyPresentation = () => {
		return [{ id: null, documentResponse: [], isDefault: false, uId: generateRandNumber() }];
	};

	useEffect(() => {
		if (designToolCommentStore?.productPresentationResponseList?.length > 0) {
			let existingData = designToolCommentStore?.productPresentationResponseList.map((item) => ({
				...item,
				uId: generateRandNumber()
			}));
			setPresentations([...existingData, ...renderEmptyPresentation()]);
		} else {
			setPresentations(renderEmptyPresentation());
		}
	}, [designToolCommentStore?.productPresentationResponseList]);

	useEffect(() => {
		dispatch(productWiseGroupDocument(id));
		setLoader(false);
	}, [id]);

	useEffect(() => {
		if (designToolStoreState && Object.keys(designToolStoreState).length > 0) {
			setDesignToolStore(designToolStoreState);
		}
	}, [designToolStoreState]);

	useEffect(() => {
		if (designToolStore?.isPresentationHidden) {
			setIsHiddenPresentation(true);
		}
	}, [designToolStore]);

	const renderFabricList = () => {
		if (designToolStore?.fabrics?.length > 1) {
			return designToolStore?.fabrics?.map((item, index) => {
				index++;
				if (index < 3) {
					return (
						<>
							<p className="regular-14">
								<span>{`Fabric ${index}`}:</span>
								<span className="bold">{item?.fabricDetailsForPresentation}</span>
							</p>
							<p className="regular-14">
								<span>Weight:</span>
								<span className="bold">{item.weight}</span>
							</p>
						</>
					);
				}
			});
		} else if (designToolStore.fabrics) {
			return (
				<>
					<p>
						<span>Fabric:</span>
						<span className="bold">
							{designToolStore.fabrics[0]?.fabricDetailsForPresentation}
						</span>
					</p>
					<p>
						<span>Weight:</span>
						<span className="bold">{designToolStore.fabrics[0]?.weight}</span>
					</p>
				</>
			);
		}
	};

	const onUploadImage = async (image) => {
		setLoader(true);
		await ApiClient.POST(`${BASE_URL}/product/${id}/upload-bulk-presentation`, image, '', true)
			.then(() => {
				dispatch(productWiseGroupDocument(id));
				setLoader(false);
			})
			.catch(({ response }) => {
				setLoader(false);
				toastError(response.data.message);
			});
	};

	const validateFileSize = (size) => {
		if (size > 1000000) {
			toastError('Please upload file less than 1mb');
			return false;
		} else {
			return true;
		}
	};

	const onUploadPresentationImage = async (e, docType, docId, presentationId) => {
		e.preventDefault();
		let file = e.target.files[0];
		if (validateFileSize(file.size)) {
			let data = {
				name: file.name,
				docMimeType: file.type,
				documentGroup: 'PHYSICAL_SAMPLE',
				documentType: docType,
				base64Str: '',
				productId: id,
				size: file.size
			};
			if (docId) {
				data.documentId = docId;
			}
			if (presentationId) {
				data.presentationId = presentationId;
			}
			let reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = async () => {
				data.base64Str = reader.result;
				try {
					dispatch(openLoader());
					await ApiClient.POST('addNewGroupDocument', data);
					await dispatch(productWiseGroupDocument(id));
				} finally {
					dispatch(closeLoader());
				}
			};
			reader.onerror = function (error) {
				toastError(error);
			};
		}
	};

	const onFileUpload = (e, presentationId) => {
		let files = [];
		let modifiedFileList = [];
		files = [...e.target.files];
		let invalidFiles = files.filter((item) => item.size > 1000000);
		if (invalidFiles.length > 0) {
			toastError('Each image size should be less than 1mb');
			return;
		}
		if (files.length !== 0 && invalidFiles.length === 0) {
			files.forEach((file) => {
				if (file) {
					if (isValidFile(file, 'PRESENTATION_UPLOAD')) {
						let data = {
							name: file?.name,
							docMimeType: file?.type,
							documentType: 'PRESENTATION_UPLOAD',
							base64Str: ''
						};
						if (presentationId) {
							data.presentationId = presentationId;
						}
						let reader = new FileReader();
						reader.readAsDataURL(file);
						reader.onload = () => {
							data.base64Str = reader.result;
							modifiedFileList.push(data);
						};
						reader.onerror = function (error) {
							toastError(error);
						};
					}
				}
			});
			setTimeout(() => {
				if (modifiedFileList?.length > 0) {
					onUploadImage(modifiedFileList);
				}
			}, 100);
		}
	};

	const onPsdFilesUpload = (e, presentationId) => {
		setLoader(true);
		let files = e.target.files;
		let totalSize = 0;

		for (let i = 0; i < files.length; i++) {
			let file = files[i];
			totalSize += file.size;
			if (totalSize > 100 * 1024 * 1024) {
				setLoader(false);
				toastWarning('Maximum file upload size is 100MB');
				return;
			}
		}

		let params = presentationId ? `${id}?presentationId=${presentationId}` : `${id}`;

		ApiClient.UPLOAD_MULTIPLE_FILE(
			'uploadPresentaionPsdFile',
			Array.from(e.target.files),
			`${params}`,
			'json',
			'files'
		)
			.then(() => {
				setLoader(false);
				toastSuccess('Successfully added');
				getPsdFiles();
			})
			.catch((error) => {
				setLoader(false);
				toastError(error.response.data.message);
			});
	};

	const getPsdFiles = () => {
		ApiClient.GET('getPresentaionPsdFiles', id)
			.then((response) => {
				const data = response.data;
				setPsdItems(data);
			})
			.catch((response) => {
				toastError(response.message);
			});
	};

	const handleToggleSidebar = () => {
		setIsOpenSidebar(!isOpenSidebar);
	};

	const renderPsdFiles = (presentationId) => {
		return psdItems
			?.filter((item) => item?.presentationId === presentationId)
			?.map((item) => {
				return (
					<div className="single-upload d-flex align-items-center" key={item.id}>
						<Tooltip title={item?.name} placement="top" arrow>
							<p
								className="regular-14 gray_dark_01 cursor-pointer"
								onClick={() => window.open(item?.docUrl, '_blank')}
							>
								{getShortName(item?.name, 12)}
							</p>
						</Tooltip>
						<div className="more-dropdown dropdown show">
							<button
								className="button text-only"
								type="button"
								id="moreInfo"
								data-toggle="dropdown"
								aria-expanded="true"
							>
								<img
									src={`${process.env.PUBLIC_URL}/icons/more_icon.svg`}
									alt="more"
									className="mr-1"
								/>
							</button>
							<ul
								className="dropdown-menu dropdown-menu-right shadow-lg"
								role="menu"
								aria-labelledby="moreInfo"
								x-placement="bottom-end"
							>
								<li>
									<span onClick={() => window.open(item?.docUrl, '_blank')}>Download</span>
								</li>
								<li>
									<span className="red" onClick={() => removePsd(item?.id)}>
										Delete
									</span>
								</li>
							</ul>
						</div>
					</div>
				);
			});
	};

	const removePsd = async (docId) => {
		setLoader(true);
		try {
			await ApiClient.DELETE('removePresentaionPsdFile', '', `${docId}`);
			setLoader(false);
			toastSuccess('Successfully removed');
			getPsdFiles();
		} catch (error) {
			setLoader(false);
			if (error.response && error.response.data && error.response.data.message) {
				toastError(error.response.data.message);
			} else {
				toastError('Request was unsuccessful.');
			}
		}
	};

	const removeImage = async (docId) => {
		setLoader(true);
		await ApiClient.DELETE('deleteProductDocument', '', `${id}/${docId}`)
			.then(async () => {
				await dispatch(productWiseGroupDocument(id));
				setLoader(false);
			})
			.catch(({ response }) => {
				setLoader(false);
				toastError(response.data.message);
			});
	};

	useEffect(() => {
		getPsdFiles();
	}, []);

	const handleDownload = async (productRef, imageUrl) => {
		const response = await fetch(imageUrl);
		const blob = await response.blob();
		const url = window.URL.createObjectURL(blob);
		// get file extension from imageUrl
		let fileExtension = '.jpg';
		if (imageUrl) {
			if (imageUrl.toLowerCase().includes('.png')) {
				fileExtension = '.png';
			} else if (imageUrl.toLowerCase().includes('.jpg') || imageUrl.toLowerCase().includes('.jpeg')) {
				fileExtension = '.jpg';
			}
		}

		const a = document.createElement('a');
		a.href = url;
		a.download = `${productRef}${fileExtension}`;
		document.body.appendChild(a);
		a.click();
		a.remove();
		window.URL.revokeObjectURL(url);
	};

	const onHidePresentation = async (isHidden) => {
		try {
			setLoader(true);
			const response = await hidePresentation(id, isHidden);
			setIsHiddenPresentation(isHidden);
			toastSuccess(response?.data?.message);
			setLoader(false);
		} catch (error) {
			printApiError(error);
		} finally {
			setLoader(false);
		}
	};

	const assignSampleIds = async (e, sampleId, presentation) => {
		try {
			dispatch(openLoader());
			const { checked } = e.target;
			await assignSamplesToProduct({
				removeAssignedSamples: !checked,
				productId: id,
				presentationId: presentation.id,
				sampleList: [sampleId]
			}).unwrap();
			await dispatch(productWiseGroupDocument(id));
		} catch {
			toastError('Something went wrong while assigning sample ids');
		} finally {
			dispatch(closeLoader());
		}
	};

	const renderSampleIdList = (presentation) => {
		let sampleNumbers =
			sampleIdList
				?.filter((item) => (presentation?.productSampleInventoryIds || []).includes(item.id))
				?.map((item) => item.sampleIdentifierNo) || [];

		return (
			<div className="sample-id-dropdown d-flex align-items-center justify-content-end">
				<div className="dropdown">
					<button
						className="btn btn-default dropdown-toggle regular-14 gray_dark_01 p-0"
						type="button"
						id="menu1"
						data-toggle="dropdown"
						aria-expanded="false"
					>
						{sampleNumbers?.join(', ') || 'Select Sample ID'}
					</button>
					<ul
						className="dropdown-menu dropdown-menu-right"
						role="menu"
						aria-labelledby="menu1"
						x-placement="bottom-end"
					>
						{sampleIdList?.map((item) => (
							<li key={`sample_${item.id}`}>
								<div className="custom-chekbox gray-checkbox px-3 py-2">
									<div className="form-group mb-0">
										<input
											type="checkbox"
											id={`presentation_${presentation?.id}_sample_${item.id}`}
											value={item.id}
											checked={
												presentation?.productSampleInventoryIds?.includes(item.id) ||
												false
											}
											onChange={(e) => assignSampleIds(e, item.id, presentation)}
										/>
										<label
											className="regular-14 d-flex align-items-center"
											htmlFor={`presentation_${presentation?.id}_sample_${item.id}`}
										>
											<span className="mb-0 regular-14 gray_dark_02">
												{item.sampleIdentifierNo}
											</span>
										</label>
									</div>
								</div>
							</li>
						))}
					</ul>
				</div>
			</div>
		);
	};

	const markAsDefaultPresentation = async (e, presentationId) => {
		try {
			dispatch(openLoader());
			const { checked } = e.target;
			if (checked) {
				await markPresentationAsDefault({ productId: id, presentationId }).unwrap();
				await dispatch(productWiseGroupDocument(id));
				toastSuccess('Successfully marked as default presentation');
			}
		} finally {
			dispatch(closeLoader());
		}
	};

	const renderPresentations = (presentationList = []) => {
		const renderPresentationActions = (item, documentId, docType) => {
			return (
				<div
					className="action-buttons d-flex gap-2 position-absolute top-2 right-2"
					key={`actions_${item.uId}`}
				>
					{documentId && isAdminOrPhotographer && (
						<button className="button size32 border-0 d-flex align-items-center justify-content-center">
							<CommonImage
								src="/icons/delete_gray.svg"
								width={24}
								height={24}
								onClick={() => removeImage(documentId)}
							/>
						</button>
					)}
					<button className="button size32 border-0 d-flex align-items-center justify-content-center">
						<div className="inline-upload">
							<label className="border-0" htmlFor={`presentation_${docType}_${item.uId}`}>
								<CommonImage src="/icons/upload-gray.svg" width={24} height={24} />
							</label>
							<input
								type="file"
								className="file-upload"
								id={`presentation_${docType}_${item.uId}`}
								name="image"
								onClick={(e) => (e.target.value = null)}
								onChange={(e) => onUploadPresentationImage(e, docType, documentId, item.id)}
								accept="image/*"
								multiple={false}
							/>
						</div>
					</button>
				</div>
			);
		};

		return presentationList?.map((item) => {
			const fontImage = item?.documentResponse?.find((document) => document.docType === 'FRONT_IMAGE');
			const backImage = item?.documentResponse?.find((document) => document.docType === 'BACK_IMAGE');
			const fabricImage = item?.documentResponse?.find(
				(document) => document.docType === 'FABRIC_IMAGE'
			);
			const embellishment = item?.documentResponse?.find(
				(document) => document.docType === 'EMBELLISHMENT'
			);
			return (
				<div className="presentation-view-container mt-0" key={`presentation-${item.uId}}`}>
					<div className="d-flex flex-wrap align-items-center justify-content-between presentation-upload-header">
						<div
							className={`upload-presentaion-and-psd d-flex align-items-center ${
								isAdminOrPhotographer ? '' : 'justify-content-center'
							}`}
						>
							{(designToolStore?.isBuyerSelected ||
								designToolStore?.availabilityStatus === 'SOLD') &&
								isUserCanHidePresentation && (
									<button
										className="button outline size32 mr-3"
										onClick={() => onHidePresentation(!isHiddenPresentation)}
									>
										<CommonImage
											src={`${isHiddenPresentation ? '/icons/eye-off.svg' : '/icons/eye.svg'}`}
											alt="delete"
										/>
									</button>
								)}

							<>
								<div className="all-presentaion mr-3">
									<div className="inline-upload">
										<label htmlFor={`bulk_presentation-${item.uId}`}>
											<CommonImage src="/icons/upload-dark.svg" />
											&nbsp; Upload Presentation
										</label>
										<input
											type="file"
											className="file-upload"
											id={`bulk_presentation-${item.uId}`}
											name="image"
											onClick={(e) => (e.target.value = null)}
											onChange={(e) => onFileUpload(e, item.id)}
											accept="image/*"
											multiple={true}
										/>
									</div>
								</div>

								<div className="d-flex align-items-center">
									<div className="upload-psd-btn">
										<div className="inline-upload">
											<label htmlFor={`bulk_PSD_presentation-${item.uId}`}>
												<CommonImage src="/icons/upload-dark.svg" />
												&nbsp; Upload PSD
											</label>
											<input
												disabled={!item?.id}
												type="file"
												className="file-upload"
												id={`bulk_PSD_presentation-${item.uId}`}
												name="image"
												onClick={(e) => (e.target.value = null)}
												onChange={(e) => onPsdFilesUpload(e, item.id)}
												accept=".psd"
												multiple={true}
											/>
										</div>
									</div>

									<div className="all-uploaded-psd gap-2">{renderPsdFiles(item.id)}</div>
									<div className="custom-chekbox ml-3">
										<div className="form-group mb-0">
											<input
												disabled={!item?.id}
												type="checkbox"
												id={`mark-default-presentation-${item.uId}`}
												name="markedAsDefault"
												className="custom-checkbox-input"
												value={item.id}
												checked={item.isDefault}
												onChange={(e) => markAsDefaultPresentation(e, item.id)}
											/>
											<label
												className="regular-14 d-flex gray_dark_02 align-items-center"
												htmlFor={`mark-default-presentation-${item.uId}`}
											>
												<span>Default presentation </span>
											</label>
										</div>
									</div>
								</div>
							</>
						</div>
						{item?.id && <p className="regular-14 gray_dark_01">Sample ID: </p>}
					</div>
					{item?.id && renderSampleIdList(item)}
					<div className="images-view-section mt-2">
						<div className="one-third full-photo position-relative">
							{fontImage?.docUrl && <img src={fontImage?.docUrl} alt="front" />}
							{renderPresentationActions(item, fontImage?.id, 'FRONT_IMAGE')}
						</div>
						<div className="one-third full-photo position-relative">
							{backImage?.docUrl && <img src={backImage?.docUrl} alt="front" />}
							{renderPresentationActions(item, backImage?.id, 'BACK_IMAGE')}
						</div>
						<div className="one-third fabric-photo">
							<div className="single-fabric position-relative">
								{fabricImage?.docUrl && <img src={fabricImage?.docUrl} alt="front" />}
								{renderPresentationActions(item, fabricImage?.id, 'FABRIC_IMAGE')}
							</div>
							<div className="single-fabric position-relative">
								{embellishment?.docUrl && <img src={embellishment?.docUrl} alt="front" />}
								{renderPresentationActions(item, embellishment?.id, 'EMBELLISHMENT')}
							</div>
						</div>
					</div>
					<div className="single-design-details">
						<div className="design-details-heading">
							<p className="regular-14">
								<span className="bold">NITEX</span> DESIGNLAB
							</p>
							<p className="regular-14">
								STYLE: <span className="bold">{designToolStore?.referenceNumber}</span>
							</p>
						</div>
						<div className="design-details-body">
							<div className="item-details py-1">
								<p className="regular-14">
									<span>ITEM:</span>
									<span className="bold">{designToolStore?.name}</span>
								</p>
								{renderFabricList()}
							</div>
						</div>
					</div>
				</div>
			);
		});
	};

	return (
		<LoaderComponent loading={isLoading || assignLoading}>
			<div className="dashboard-container presentation-container bg-white pb-4">
				<div>
					{presentations?.length > 0 && renderPresentations([presentations[0]])}
					{isOpenSidebar && (
						<VirtualTryOnSidebar
							onClose={handleToggleSidebar}
							productId={id}
							frontImageUrl={designToolCommentStore?.documentResponse?.front?.docUrl}
							backImageUrl={designToolCommentStore?.documentResponse?.back?.docUrl}
							referenceName={designToolStore?.referenceNumber}
							productMarket={designToolStore?.productGroup}
							parentCatergory={designToolStore?.parentCatergory}
							onDownload={handleDownload}
						/>
					)}
				</div>
				<div className="presentations-tab">
					<div className="presentations-upload-image-container virtual-try-on-container d-grid grid-columns-5">
						<AiModelCard
							onOpenSidebar={handleToggleSidebar}
							hasEditPermission={isAdminOrPhotographer}
							imageUrl={designToolCommentStore?.virtualTryOnDocResponse?.docUrl}
							onDownload={() =>
								handleDownload(
									designToolStore?.referenceNumber,
									designToolCommentStore?.virtualTryOnDocResponse?.docUrl
								)
							}
						/>
					</div>
				</div>
				{presentations?.length > 1 && renderPresentations(presentations.slice(1))}
			</div>
		</LoaderComponent>
	);
};

export default Presentation;
